// ============================================================================
// STRICT MODULE HEADER — Strict, opinionated header template for ALL modules
//
// This is the canonical, strict header template that enforces a consistent
// layout across every module view. It reuses the same layout-prefs /
// module-color / favourites infrastructure as ModuleHeader but renders a
// rigidly structured layout:
//
//   Row A — title bar: [nav toggle] [icon + title + titleExtra (flex, max 2 lines)] [pills centred, z-10] [actions + actionsExtra slot]
//           Title flexes up to 420px (wide when few pills) and clamps to 2
//           lines via line-clamp-2 — it never grows the row taller. Pills
//           render at z-10 above the title so a long title + many pills
//           prioritise the pills, not the title.
//   Row C — stat cards: exactly 4 or 6, NON-clickable, label + value + badges
//           When 0 stat cards, the row height is reserved so the header stays
//           the same size across all views.
//           Minimise chevron hides ONLY this row (matches ModuleHeader dashboard).
//   Row D — filter pills + filter slot: contiguous pill bar (no gaps) for
//           status/view filters, plus optional custom filter UI below.
//           Always visible — not affected by minimise or vertical nav.
//
// Strictness rules (enforced in dev via console warnings):
//   - statCards: 0, 4, or 6 only (never 3, 5, 8, ...)
//   - actions:   0–4 only (3 ghost icon-only + 1 solid +New)
//   - stat cards are never clickable (display only)
//   - pills never change width/spacing when active state changes
//   - header colour is only the module colour or client branding colour
//   - minimise chevron: hides stat cards only (pills + filters stay)
//   - vertical nav: hides pills (move to sidebar) + stat cards; filters stay
//     (the 'dashboard' variant keeps stat cards visible — dashboards never
//     render Row D pills, so the freed space hosts the stats instead)
//
// Date: September 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  ChevronUp, ChevronDown, Plus, Star, PanelTop, PanelLeft,
  HelpCircle, ArrowUpLeft, ArrowDownRight, RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SHELL_TOGGLE_BUTTON_CLASS, SHELL_TOGGLE_ICON_CLASS } from '@/components/shared/shellToggleButtonStyles';
import { useFavourites } from '@/hooks/useFavourites';
import { useModuleColor } from '@/hooks/useModuleColor';
import { useClearRedirectParams } from '@/hooks/useClearRedirectParams';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

// ---------------------------------------------------------------------------
// Layout prefs — identical key/event to ModuleHeader so the nav-layout-mode
// toggle stays in sync across the whole app. Duplicated (not imported) to
// avoid coupling; the strings MUST match ModuleHeader.tsx.
// ---------------------------------------------------------------------------
const LAYOUT_PREFS_KEY = 'ta_ui_layout_prefs';
const LAYOUT_PREFS_EVENT = 'ta-ui-layout-prefs-changed';

type LayoutPrefs = Record<string, unknown>;

function readLayoutPrefs(): LayoutPrefs {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(LAYOUT_PREFS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeLayoutPrefs(updates: LayoutPrefs) {
  if (typeof window === 'undefined') return;
  const nextPrefs = { ...readLayoutPrefs(), ...updates };
  localStorage.setItem(LAYOUT_PREFS_KEY, JSON.stringify(nextPrefs));
  window.dispatchEvent(new CustomEvent(LAYOUT_PREFS_EVENT, {
    detail: { changedKeys: Object.keys(updates), prefs: nextPrefs },
  }));
}

// ============================================================================
// TYPES
// ============================================================================

export type StrictBadgeTone = 'default' | 'success' | 'warning' | 'danger' | 'info';

export interface StrictStatBadge {
  label: string;
  value: string | number;
  tone?: StrictBadgeTone;
}

export interface StrictStatCard {
  id: string;
  label: string;
  /** Primary large value */
  value: string | number;
  icon?: React.ElementType;
  /** 3–4 small coloured badge stats shown alongside the primary value */
  badges?: StrictStatBadge[];
}

// ---------------------------------------------------------------------------
// Strict stat-card count enforcement: 0, 4, or 6 only.
//
// `strictCards()` is a runtime + dev-time validator. In dev mode it throws
// if the count is not 0, 4, or 6. In production it silently passes through.
//
//   const cards = strictCards([a, b, c, d]);        // OK (4)
//   const cards = strictCards([a, b, c, d, e]);     // Dev: throws
//   const cards = strictCards([]);                  // OK (0)
//
// The prop type remains `StrictStatCard[]` for backwards compatibility with
// the 90+ existing callers, but the render layer clamps any invalid count
// to the nearest valid value (4 or 6) so the layout never breaks.
// ---------------------------------------------------------------------------

/**
 * Validate a stat-card array as having exactly 0, 4, or 6 entries.
 * Throws at runtime in dev mode if the count is invalid.
 * In production, silently returns the array as-is (render layer will clamp).
 */
export function strictCards(cards: StrictStatCard[]): StrictStatCard[] {
  if (process.env.NODE_ENV !== 'production') {
    const count = cards.length;
    if (count !== 0 && count !== 4 && count !== 6) {
      throw new Error(
        `[StrictModuleHeader] statCards must be 0, 4, or 6 — received ${count}.\n` +
        `Fix the caller to produce exactly 0, 4, or 6 cards.\n` +
        `Cards: ${cards.map((c) => c.id).join(', ')}`,
      );
    }
  }
  return cards;
}

export interface StrictPill {
  id: string;
  label: string;
  icon?: React.ElementType;
  /** Subtle dot/underline accent colour for this pill (hex) */
  accentColor?: string;
  dataTourId?: string;
  /** Optional status chip rendered inside the pill — used by pipeline-style
   *  hubs (e.g. PayrollHub) to show "3 pending" / "Draft" / "Approved" state. */
  badge?: string;
  badgeTone?: StrictBadgeTone;
}

export interface StrictFilterPill {
  id: string;
  label: string;
  icon?: React.ElementType;
  active?: boolean;
  onClick?: () => void;
  separatorBefore?: boolean;
  dataTourId?: string;
}

export interface StrictActionButton {
  id: string;
  icon: React.ElementType;
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** `solid` = white pill (e.g. +New), `ghost` = translucent icon button */
  variant?: 'solid' | 'ghost';
  title?: string;
  dataTourId?: string;
}

export interface StrictModuleHeaderProps {
  // ---- Identity ----
  icon: React.ElementType;
  title: string;
  subtitle?: string;

  // ---- Colour (module colour OR client branding colour) ----
  moduleKey?: string;
  primaryColor?: string;
  backgroundClassName?: string;

  // ---- Variant ----
  /** `dashboard` keeps Row C stat cards visible in vertical-nav mode —
   *  dashboards never render Row D pills, so the freed space hosts the
   *  stats instead. Default headers collapse to a single row in vertical
   *  nav (pills move to the sidebar, stat cards hide). */
  variant?: 'default' | 'dashboard';

  // ---- Row B: pills (centre-aligned, equal-width, wrap) ----
  pills: StrictPill[];
  activePill: string | null;
  onPillChange: (id: string | null) => void;

  // ---- Row C: stat cards (0, 4, or 6 only; non-clickable) ----
  /** Must have 0, 4, or 6 entries. Use `strictCards()` to enforce at dev time.
   *  The render layer clamps invalid counts to the nearest valid value. */
  statCards?: StrictStatCard[];

  // ---- Row D: contextual filter pills for the active pill ----
  /** Structured filter pills rendered in a contiguous pill bar (no gaps).
   *  Use this for pill-style filters (status tabs, view toggles, etc.). */
  filterPills?: StrictFilterPill[];

  /** Host-rendered custom filter UI for the active pill. Rendered below
   *  filterPills when both are provided. When null/empty, Row D only shows
   *  filterPills (if any). */
  filtersSlot?: ReactNode;

  // ---- Row A extras ----
  /** Optional node rendered inline after the title (Row A, top-left) —
   *  intended for contextual help links such as <UserDocsLink/>. Rendered
   *  on the coloured header, so pass a light-on-colour variant
   *  (e.g. className="text-white/80 hover:text-white"). */
  titleExtra?: ReactNode;

  /** Optional node rendered at the end of the actions row (Row A,
   *  top-right, after the +New/ghost buttons) — e.g. a contextual help
   *  icon that belongs with the header actions rather than the title. */
  actionsExtra?: ReactNode;

  // ---- Row A right: action buttons (0–4 only; 3 ghost + 1 solid +New) ----
  actions?: StrictActionButton[];

  // ---- Convenience: +New shortcut (always position 1, rightmost, solid white) ----
  showNewButton?: boolean;
  newButtonLabel?: string;
  onNew?: () => void;
  newButtonDataTourId?: string;

  // ---- Convenience: refresh shortcut (icon-only ghost, position 2) ----
  showSyncButton?: boolean;
  onSync?: () => void;
  syncing?: boolean;

  // ---- Collapse behaviour ----
  storageKey?: string;
  defaultMinimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;

  // ---- Layout sync control (collapses header + sidenav together) ----
  showCombinedLayoutToggle?: boolean;

  // ---- Favourites ----
  favouriteConfig?: {
    label: string;
    href: string;
    moduleKey?: string;
    section?: string;
  };

  // ---- Help / tour ----
  onHelpClick?: () => void;
  helpButtonTooltip?: string;
  componentTourConfig?: {
    moduleKey: string;
    flowId: string;
    stepId?: string;
    objectiveId?: string;
    tooltip?: string;
  };

  // ---- One-shot redirect query params ----
  clearQueryParamsOnChange?: string[];

  // ---- Visibility ----
  hidden?: boolean;
  className?: string;
}

// ============================================================================
// BADGE TONE STYLES (on coloured header → translucent white base + tone accent)
// ============================================================================
const BADGE_TONE_CLASS: Record<StrictBadgeTone, string> = {
  default: 'bg-white/15 text-white/90 ring-white/20',
  success: 'bg-emerald-400/25 text-emerald-50 ring-emerald-300/40',
  warning: 'bg-amber-400/25 text-amber-50 ring-amber-300/40',
  danger: 'bg-rose-400/25 text-rose-50 ring-rose-300/40',
  info: 'bg-sky-400/25 text-sky-50 ring-sky-300/40',
};

// Same tones on a light surface — active pills are white, the mobile pill
// dropdown is white, so the translucent-on-colour variants wash out.
const BADGE_TONE_LIGHT_CLASS: Record<StrictBadgeTone, string> = {
  default: 'bg-gray-200 text-gray-600 ring-gray-300',
  success: 'bg-emerald-100 text-emerald-700 ring-emerald-300',
  warning: 'bg-amber-100 text-amber-700 ring-amber-300',
  danger: 'bg-rose-100 text-rose-700 ring-rose-300',
  info: 'bg-sky-100 text-sky-700 ring-sky-300',
};

function pillBadgeClass(tone: StrictBadgeTone | undefined, onLight: boolean): string {
  return `ml-0.5 rounded-full px-1.5 py-px text-[10px] font-semibold leading-4 ring-1 ${(onLight ? BADGE_TONE_LIGHT_CLASS : BADGE_TONE_CLASS)[tone ?? 'default']}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function StrictModuleHeader({
  icon: Icon,
  title,
  subtitle,
  moduleKey,
  primaryColor,
  backgroundClassName,
  variant = 'default',
  pills,
  activePill,
  onPillChange,
  statCards,
  filterPills,
  filtersSlot,
  titleExtra,
  actionsExtra,
  actions = [],
  showNewButton,
  newButtonLabel = 'New Item',
  onNew,
  newButtonDataTourId,
  showSyncButton,
  onSync,
  syncing,
  storageKey = 'ta_strict_module_header',
  defaultMinimized = false,
  onMinimizedChange,
  showCombinedLayoutToggle = false,
  favouriteConfig,
  onHelpClick,
  helpButtonTooltip,
  componentTourConfig,
  clearQueryParamsOnChange,
  hidden = false,
  className,
}: StrictModuleHeaderProps) {
  // ---------- Dev-time strictness checks ----------
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const cardCount = statCards?.length ?? 0;
    if (cardCount !== 0 && cardCount !== 4 && cardCount !== 6) {
      console.error(
        `[StrictModuleHeader] statCards must be 0, 4, or 6 — received ${cardCount}. ` +
        `Component: "${title}". The render will be clamped to the nearest valid count.`,
      );
    }
  }, [statCards, title]);

  // ---------- Render-time clamping: enforce 0/4/6 regardless of input ----------
  const clampedStatCards = useMemo<StrictStatCard[] | undefined>(() => {
    if (!statCards || statCards.length === 0) return undefined;
    const count = statCards.length;
    if (count === 4 || count === 6) return statCards;
    // Clamp to nearest valid count: 1-2 → 4 (pad), 3 → 4, 5 → 6 (pad), 7+ → 6
    if (count <= 4) {
      // Pad to 4 with empty placeholders
      const padded = [...statCards];
      while (padded.length < 4) {
        padded.push({ id: `_pad_${padded.length}`, label: '', value: '' });
      }
      return padded.slice(0, 4);
    }
    // 5 or 7+: clamp to 6
    const padded = [...statCards];
    while (padded.length < 6) {
      padded.push({ id: `_pad_${padded.length}`, label: '', value: '' });
    }
    return padded.slice(0, 6);
  }, [statCards]);

  // ---------- State ----------
  const [minimized, setMinimized] = useState<boolean>(() => {
    const storedValue = readLayoutPrefs()[storageKey];
    return typeof storedValue === 'boolean' ? storedValue : defaultMinimized;
  });
  const [sideNavCollapsed, setSideNavCollapsed] = useState(() => Boolean(readLayoutPrefs().sideNavCollapsed));
  const [navLayoutMode, setNavLayoutMode] = useState<'horizontal' | 'vertical'>(() => {
    const stored = readLayoutPrefs().navLayoutMode;
    return stored === 'vertical' ? 'vertical' : 'horizontal';
  });

  const { isFavourite, toggleFavourite, setBarVisible } = useFavourites();
  const { moduleColor } = useModuleColor(moduleKey ?? '', primaryColor || '');
  const isFav = favouriteConfig ? isFavourite(favouriteConfig.href, favouriteConfig.section) : false;
  const resolvedPrimaryColor = primaryColor || moduleColor || undefined;

  // ---------- One-shot redirect param clearing ----------
  const clearRedirectParams = useClearRedirectParams(clearQueryParamsOnChange ?? []);
  const handlePillChange = useCallback((id: string | null) => {
    clearRedirectParams();
    onPillChange(id);
  }, [clearRedirectParams, onPillChange]);

  // ---------- Persist minimised ----------
  useEffect(() => {
    writeLayoutPrefs({ [storageKey]: minimized });
    onMinimizedChange?.(minimized);
  }, [minimized, storageKey, onMinimizedChange]);

  // ---------- Sync from prefs (cross-component) ----------
  useEffect(() => {
    const syncFromPrefs = (prefs?: LayoutPrefs) => {
      const resolvedPrefs = prefs ?? readLayoutPrefs();
      const storedMinimized = resolvedPrefs[storageKey];
      const nextMinimized = typeof storedMinimized === 'boolean' ? storedMinimized : defaultMinimized;
      const nextSideNavCollapsed = Boolean(resolvedPrefs.sideNavCollapsed);
      const nextNavLayoutMode = resolvedPrefs.navLayoutMode === 'vertical' ? 'vertical' : 'horizontal';
      setMinimized((prev) => (prev === nextMinimized ? prev : nextMinimized));
      setSideNavCollapsed((prev) => (prev === nextSideNavCollapsed ? prev : nextSideNavCollapsed));
      setNavLayoutMode((prev) => (prev === nextNavLayoutMode ? prev : nextNavLayoutMode));
    };
    const handlePrefsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ changedKeys?: string[]; prefs?: LayoutPrefs }>).detail;
      if (!detail?.changedKeys ||
        detail.changedKeys.includes(storageKey) ||
        detail.changedKeys.includes('sideNavCollapsed') ||
        detail.changedKeys.includes('navLayoutMode')) {
        syncFromPrefs(detail?.prefs);
      }
    };
    const handleStorage = () => syncFromPrefs();
    window.addEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [defaultMinimized, storageKey]);

  const toggleMinimized = useCallback(() => setMinimized((prev) => !prev), []);

  const toggleCombinedLayout = useCallback(() => {
    const allCollapsed = minimized && sideNavCollapsed;
    const nextCollapsed = !allCollapsed;
    setMinimized(nextCollapsed);
    setSideNavCollapsed(nextCollapsed);
    writeLayoutPrefs({ [storageKey]: nextCollapsed, sideNavCollapsed: nextCollapsed });
  }, [minimized, sideNavCollapsed, storageKey]);

  const toggleNavLayoutMode = useCallback(() => {
    const next = navLayoutMode === 'horizontal' ? 'vertical' : 'horizontal';
    writeLayoutPrefs({ navLayoutMode: next });
    setNavLayoutMode(next);
  }, [navLayoutMode]);

  const isVerticalNav = navLayoutMode === 'vertical';

  const handleToggleFavourite = useCallback(() => {
    if (!favouriteConfig) return;
    const added = toggleFavourite({
      label: favouriteConfig.label,
      href: favouriteConfig.href,
      moduleKey: favouriteConfig.moduleKey,
      section: favouriteConfig.section,
    });
    if (added) setBarVisible(true);
  }, [favouriteConfig, setBarVisible, toggleFavourite]);

  // ---------- Assemble actions (with strict 0–4 limit) ----------
  // Order: explicit `actions` first (ghost icon-only), then sync (ghost),
  // then +New (solid white, rightmost).
  // Visual layout: [4][3][2][1] = [ghost][ghost][ghost][+New]
  const orderedActions = useMemo<StrictActionButton[]>(() => {
    const list: StrictActionButton[] = [...(Array.isArray(actions) ? actions : [])];
    if (showSyncButton && onSync) {
      list.push({
        id: '_sync',
        icon: RefreshCw,
        onClick: onSync,
        loading: syncing,
        variant: 'ghost',
        title: 'Refresh',
      });
    }
    if (showNewButton && onNew) {
      list.push({
        id: '_new',
        icon: Plus,
        label: newButtonLabel,
        onClick: onNew,
        variant: 'solid',
        dataTourId: newButtonDataTourId,
      });
    }
    return list;
  }, [actions, showSyncButton, onSync, syncing, showNewButton, onNew, newButtonLabel, newButtonDataTourId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (orderedActions.length > 4) {
      console.warn(
        `[StrictModuleHeader] actions must be 0–4 — received ${orderedActions.length}. ` +
        `Component: "${title}". Only the first 4 will render.`,
      );
    }
  }, [orderedActions.length, title]);

  const visibleActions = orderedActions.slice(0, 4);

  // ---------- Derived flags ----------
  const showStatCards = clampedStatCards && clampedStatCards.length > 0;
  const hasFilterPills = filterPills && filterPills.length > 0;
  // Minimise chevron: ONLY hides stat cards (Row C). Pills (Row B) and filter
  // slot (Row D) stay visible — matches ModuleHeader dashboard variant behaviour
  // where the chevron hides stats but keeps the title bar + type filter tabs.
  // Vertical nav: hides pills (they move to sidebar) + stat cards, but keeps
  // the filter slot visible. The dashboard variant keeps stat cards visible
  // in vertical nav — dashboards never render Row D pills, so the freed
  // space hosts the stats instead.
  const isDashboardVariant = variant === 'dashboard';
  const areStatsHidden = minimized || (isVerticalNav && !isDashboardVariant);
  const arePillsHidden = isVerticalNav;

  // When vertical nav is active, show the active pill label as the title
  const activePillLabel = pills.find((p) => p.id === activePill)?.label;
  const displayTitle = isVerticalNav && activePillLabel ? activePillLabel : title;

  // ---------- Mobile viewport detection (must be before any conditional return) ----------
  const isMobileViewport = useIsMobileViewport();
  const [mobilePillDropdownOpen, setMobilePillDropdownOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const mobilePillDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMoreRef = useRef<HTMLDivElement>(null);
  const mobileMoreBtnRef = useRef<HTMLButtonElement>(null);

  // Close mobile dropdowns on outside click
  useEffect(() => {
    if (!mobilePillDropdownOpen && !mobileMoreOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (mobilePillDropdownOpen && mobilePillDropdownRef.current && !mobilePillDropdownRef.current.contains(target)) {
        setMobilePillDropdownOpen(false);
      }
      if (mobileMoreOpen && mobileMoreRef.current && mobileMoreBtnRef.current
        && !mobileMoreRef.current.contains(target) && !mobileMoreBtnRef.current.contains(target)) {
        setMobileMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobilePillDropdownOpen, mobileMoreOpen]);

  if (hidden) return null;

  // ---------- Background ----------
  const bgStyle: React.CSSProperties = !backgroundClassName && resolvedPrimaryColor
    ? { backgroundColor: resolvedPrimaryColor }
    : {};
  const bgClass = backgroundClassName || (resolvedPrimaryColor ? '' : 'bg-gradient-to-r from-blue-500 to-indigo-600');

  // Stat-card grid columns: 4 → 4 cols, 6 → 6 cols (responsive collapse to 2)
  const statGridClass = (clampedStatCards?.length ?? 0) === 6
    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
    : 'grid-cols-2 sm:grid-cols-4';

  // ==========================================================================
  // RENDER: PILLS (Row B) — styled same as filter slot pills
  // ==========================================================================
  const renderPills = () => (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {pills.map((pill) => {
        const PillIcon = pill.icon;
        const isActive = activePill === pill.id;
        return (
          <button
            key={pill.id}
            onClick={() => handlePillChange(isActive ? null : pill.id)}
            data-tour-id={pill.dataTourId}
            className={cn(
              'relative flex items-center justify-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-white text-gray-800 shadow'
                : 'bg-white/15 text-white/90 hover:bg-white/25',
            )}
          >
            {PillIcon && <PillIcon className="h-3 w-3 flex-shrink-0" />}
            <span className="truncate">{pill.label}</span>
            {pill.badge && (
              <span className={pillBadgeClass(pill.badgeTone, isActive)}>{pill.badge}</span>
            )}
            {pill.accentColor && isActive && (
              <span
                className="absolute -bottom-0.5 left-1/2 h-[3px] w-[60%] -translate-x-1/2 rounded-full"
                style={{ backgroundColor: pill.accentColor }}
              />
            )}
          </button>
        );
      })}
    </div>
  );

  // ==========================================================================
  // RENDER: STAT CARDS (Row C) — non-clickable
  // ==========================================================================
  const renderStatCards = () => {
    if (!showStatCards) return null;
    return (
      <div className={cn('grid gap-2', statGridClass)}>
        {clampedStatCards!.map((card) => {
          const CardIcon = card.icon;
          return (
            <div
              key={card.id}
              data-tour-id={card.id}
              className="relative flex flex-col gap-1.5 rounded-lg bg-white/15 px-2.5 py-2 backdrop-blur-sm"
            >
              <div className="flex items-center gap-2">
                {CardIcon && (
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                    <CardIcon className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-medium text-white/70">{card.label}</div>
                  <div className="text-base font-bold leading-tight text-white">
                    {typeof card.value === 'number' && !Number.isFinite(card.value) ? '—' : card.value}
                  </div>
                </div>
              </div>
              {card.badges && card.badges.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {card.badges.map((badge, i) => (
                    <span
                      key={`${badge.label}-${i}`}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1',
                        BADGE_TONE_CLASS[badge.tone ?? 'default'],
                      )}
                    >
                      <span className="text-white/70">{badge.label}</span>
                      <span className="font-semibold">{badge.value}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ==========================================================================
  // RENDER: ACTIONS (Row A right) — fixed-width reserved slot
  // Layout: [4][3][2][1] = [ghost][ghost][ghost][+New solid]
  // Ghost buttons are icon-only with translucent module colour (same as stat
  // cards). Solid +New is white with label text, rightmost.
  // ==========================================================================
  const renderActions = () => (
    <div className="flex h-9 w-[220px] min-w-[220px] max-w-[220px] flex-nowrap items-center justify-end gap-1.5 overflow-hidden">
      {visibleActions.map((action) => {
        const ActionIcon = action.icon;
        const isSolid = action.variant === 'solid';
        if (isSolid) {
          return (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled || action.loading}
              title={action.title || action.label}
              data-tour-id={action.dataTourId}
              className="flex h-9 flex-shrink items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-2.5 text-xs font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <ActionIcon className={cn('h-4 w-4 flex-shrink-0', action.loading && 'animate-spin')} />
              {action.label && <span className="hidden truncate sm:inline">{action.label}</span>}
            </button>
          );
        }
        return (
          <button
            key={action.id}
            onClick={action.onClick}
            disabled={action.disabled || action.loading}
            title={action.title || action.label}
            data-tour-id={action.dataTourId}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25 disabled:opacity-50"
          >
            <ActionIcon className={cn('h-4 w-4', action.loading && 'animate-spin')} />
          </button>
        );
      })}
    </div>
  );

  // ==========================================================================
  // RENDER: FILTER PILLS (Row D) — contiguous pill bar, no gaps
  // Matches ContactManagementHub style: bg-white/10 container, rounded-lg
  // pills, active = white bg + dark text, inactive = translucent.
  // ==========================================================================
  const renderFilterPills = () => {
    if (!hasFilterPills) return null;
    return (
      <div className="flex w-full justify-center">
        <div className="flex items-center gap-0 overflow-x-auto scrollbar-thin scrollbar-thumb-white/30 rounded-none bg-white/10 p-0.5 max-w-full">
          {filterPills!.map((pill) => {
            const FilterIcon = pill.icon;
            return (
              <button
                key={pill.id}
                onClick={pill.onClick}
                data-tour-id={pill.dataTourId}
                data-active={pill.active}
                className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors ${pill.active
                  ? 'bg-white text-gray-800 shadow-lg'
                  : 'text-white/90 hover:bg-white/20'
                  } ${pill.separatorBefore ? 'ml-2 border-l border-white/20 pl-2' : ''}`}
              >
                {FilterIcon && <FilterIcon className="w-4 h-4" />}
                <span>{pill.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ==========================================================================
  // MOBILE RENDER — compact header bar with pill dropdown (SmartFinance pattern)
  // ==========================================================================
  if (isMobileViewport) {
    const hasPills = pills.length > 0;
    const hasActions = visibleActions.length > 0;
    const hasStats = clampedStatCards && clampedStatCards.length > 0;
    const hasFilters = Boolean(hasFilterPills || filtersSlot);
    const hasMore = hasStats || hasFilters;

    // Find the active pill meta for display
    const activePillMeta = hasPills ? pills.find(p => p.id === activePill) : null;
    const activePillLabel = activePillMeta?.label ?? displayTitle;
    const ActivePillIcon = activePillMeta?.icon ?? Icon;

    // Don't render if there's literally nothing to show
    if (!hasPills && !hasActions && !hasStats && !hasFilters) return null;

    return (
      <div
        className={cn('flex-shrink-0 text-white', bgClass)}
        style={bgStyle}
      >
        {/* Compact single-row header */}
        <div className="flex items-center gap-2 px-3 py-2 safe-top">
          {/* Icon */}
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/15">
            <Icon className="h-4 w-4 text-white" />
          </div>

          {/* Title or pill dropdown */}
          {hasPills ? (
            <div ref={mobilePillDropdownRef} className="relative min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setMobilePillDropdownOpen(o => !o)}
                className="flex min-w-0 items-center gap-1.5 rounded-lg bg-white/10 px-2 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-white/20"
                aria-label={`Switch view (currently ${activePillLabel})`}
              >
                <ActivePillIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="min-w-0 truncate">{activePillLabel}</span>
                <ChevronDown className={cn('h-3 w-3 flex-shrink-0 transition-transform', mobilePillDropdownOpen && 'rotate-180')} />
              </button>

              {mobilePillDropdownOpen && (
                <div className="absolute left-0 top-full z-[9999] mt-1 max-w-[calc(100vw-1rem)] rounded-xl border border-gray-200 bg-white py-1 shadow-2xl dark:border-gray-600 dark:bg-gray-800" style={{ minWidth: '12rem' }}>
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Switch View
                  </p>
                  {pills.map((pill) => {
                    const PillIcon = pill.icon;
                    const isActive = activePill === pill.id;
                    return (
                      <button
                        key={pill.id}
                        onClick={() => {
                          handlePillChange(isActive ? null : pill.id);
                          setMobilePillDropdownOpen(false);
                        }}
                        data-tour-id={pill.dataTourId}
                        className={cn(
                          'flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50',
                        )}
                      >
                        {PillIcon && <PillIcon className="h-4 w-4 flex-shrink-0" style={isActive ? { color: resolvedPrimaryColor || '#2563eb' } : undefined} />}
                        <span className="truncate">{pill.label}</span>
                        {pill.badge && (
                          <span className={pillBadgeClass(pill.badgeTone, true)}>{pill.badge}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-sm font-bold text-white">{displayTitle}</h2>
            </div>
          )}

          {/* Favourite star */}
          {favouriteConfig && (
            <button
              onClick={handleToggleFavourite}
              className="flex-shrink-0 rounded-md p-1.5 text-white/80 transition-all hover:bg-white/10"
              title={isFav ? 'Remove from favourites' : 'Add to favourites'}
            >
              <Star className={cn('h-4 w-4', isFav && 'fill-amber-300 text-amber-300')} />
            </button>
          )}

          {/* Sync button */}
          {showSyncButton && onSync && (
            <button
              onClick={onSync}
              disabled={syncing}
              className="flex-shrink-0 rounded-md p-1.5 text-white/80 transition-all hover:bg-white/10 disabled:opacity-50"
              title="Sync"
            >
              <RefreshCw className={cn('h-4 w-4', syncing && 'animate-spin')} />
            </button>
          )}

          {/* New button */}
          {showNewButton && onNew && (
            <button
              onClick={onNew}
              className="flex-shrink-0 rounded-md bg-white/20 p-1.5 text-white transition-all hover:bg-white/30"
              title={newButtonLabel || 'New'}
            >
              <Plus className="h-4 w-4" />
            </button>
          )}

          {/* Action buttons (icon-only, max 2 inline) */}
          {hasActions && visibleActions.slice(0, 2).map((action) => {
            const ActionIcon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                title={action.title || action.label}
                data-tour-id={action.dataTourId}
                className="flex-shrink-0 rounded-md p-1.5 text-white/80 transition-all hover:bg-white/10 disabled:opacity-50"
              >
                <ActionIcon className={cn('h-4 w-4', action.loading && 'animate-spin')} />
              </button>
            );
          })}

          {/* More button for stats/filters/extra actions */}
          {(hasMore || (hasActions && visibleActions.length > 2)) && (
            <div ref={mobileMoreRef} className="relative flex-shrink-0">
              <button
                ref={mobileMoreBtnRef}
                type="button"
                onClick={() => setMobileMoreOpen(o => !o)}
                className="rounded-md p-1.5 text-white/80 transition-all hover:bg-white/10"
                title="More"
              >
                <ChevronDown className={cn('h-4 w-4 transition-transform', mobileMoreOpen && 'rotate-180')} />
              </button>

              {mobileMoreOpen && (
                <div className="absolute right-0 top-full z-[9999] mt-1 w-56 max-w-[calc(100vw-1rem)] rounded-xl border border-gray-200 bg-white py-1 shadow-2xl dark:border-gray-600 dark:bg-gray-800">
                  {/* Extra actions */}
                  {hasActions && visibleActions.length > 2 && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Actions
                      </p>
                      {visibleActions.slice(2).map((action) => {
                        const ActionIcon = action.icon;
                        return (
                          <button
                            key={action.id}
                            onClick={() => {
                              action.onClick();
                              setMobileMoreOpen(false);
                            }}
                            disabled={action.disabled || action.loading}
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50 disabled:opacity-50"
                          >
                            <ActionIcon className={cn('h-4 w-4 flex-shrink-0', action.loading && 'animate-spin')} />
                            <span className="truncate">{action.label || action.title}</span>
                          </button>
                        );
                      })}
                    </>
                  )}

                  {/* Filter pills */}
                  {hasFilterPills && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Filters
                      </p>
                      <div className="flex flex-wrap gap-1.5 px-3 pb-2">
                        {filterPills!.map((pill) => {
                          const FilterIcon = pill.icon;
                          return (
                            <button
                              key={pill.id}
                              onClick={pill.onClick}
                              data-tour-id={pill.dataTourId}
                              className={cn(
                                'flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium whitespace-nowrap transition-colors',
                                pill.active
                                  ? 'text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300',
                              )}
                              style={pill.active ? { backgroundColor: resolvedPrimaryColor || '#2563eb' } : undefined}
                            >
                              {FilterIcon && <FilterIcon className="h-3 w-3" />}
                              {pill.label}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Custom filters slot */}
                  {filtersSlot && (
                    <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
                      {filtersSlot}
                    </div>
                  )}

                  {/* Stat cards */}
                  {hasStats && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Stats
                      </p>
                      <div className="grid grid-cols-2 gap-2 px-3 pb-2">
                        {clampedStatCards!.map((card) => {
                          const CardIcon = card.icon;
                          return (
                            <div
                              key={card.id}
                              data-tour-id={card.id}
                              className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-700/50"
                            >
                              {CardIcon && (
                                <div
                                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md"
                                  style={{ backgroundColor: (resolvedPrimaryColor || '#2563eb') + '20' }}
                                >
                                  <CardIcon className="h-3.5 w-3.5" style={{ color: resolvedPrimaryColor || '#2563eb' }} />
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">{card.label}</div>
                                <div className="text-sm font-bold text-gray-900 dark:text-white">{card.value}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter pills row (inline, not in dropdown — they're important for navigation) */}
        {hasFilterPills && (
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide px-3 pb-2">
            {filterPills!.map((pill) => {
              const FilterIcon = pill.icon;
              return (
                <button
                  key={pill.id}
                  onClick={pill.onClick}
                  data-tour-id={pill.dataTourId}
                  className={cn(
                    'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-colors',
                    pill.active
                      ? 'bg-white/25 text-white'
                      : 'bg-white/10 text-white/80 hover:bg-white/20',
                  )}
                >
                  {FilterIcon && <FilterIcon className="h-3 w-3" />}
                  {pill.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Custom filters slot (inline) */}
        {filtersSlot && (
          <div className="px-3 pb-2">
            {filtersSlot}
          </div>
        )}
      </div>
    );
  }

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================
  return (
    <div
      className={cn('relative flex-shrink-0 text-white transition-all duration-200', bgClass, className)}
      style={bgStyle}
    >
      {/* ================================================================ */}
      {/* ROW A — TITLE BAR + PILLS                                         */}
      {/* Title is in a fixed-width div (wraps to 2 lines) so it never       */}
      {/* pushes the centred pills out of position when navigating between   */}
      {/* views with different title lengths.                               */}
      {/* Pills stay visible when minimised (only stat cards hide).          */}
      {/* ================================================================ */}
      <div className={cn('relative flex items-start justify-between gap-3 px-4', isVerticalNav ? 'py-1.5 sm:py-2' : 'py-2 sm:py-3')}>
        {/* Left: nav toggle + icon + title (fixed-width, wraps to 2 lines).
            Narrow slot — long titles clamp with ellipsis so they never push
            the stat cards down or squeeze the centred pills. */}
        <div className="relative z-0 flex min-w-[140px] max-w-[260px] items-start gap-2 sm:gap-3">
          {showCombinedLayoutToggle && (
            <button
              onClick={toggleNavLayoutMode}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white/70 transition-all hover:bg-white/10 hover:text-white sm:h-9 sm:w-9"
              title={isVerticalNav ? 'Switch to horizontal navigation (pills in header)' : 'Switch to vertical navigation (sidebar)'}
            >
              {isVerticalNav ? <PanelLeft className="h-5 w-5" /> : <PanelTop className="h-5 w-5" />}
            </button>
          )}
          <Icon className="h-6 w-6 flex-shrink-0 text-white/90 sm:h-7 sm:w-7" />
          <div className="min-w-0 flex-1">
            {/* min-h = a 2-line title row so the help/favourite icons always
                sit at the same vertical position regardless of title length
                (and their tooltips have room above inside the header). */}
            <div className="flex min-h-[40px] items-center gap-1.5 sm:min-h-[45px]">
              <h1 className="line-clamp-2 text-base font-bold leading-tight text-white sm:text-lg" title={displayTitle}>{displayTitle}</h1>
              {titleExtra}
              {favouriteConfig && (
                <button
                  onClick={handleToggleFavourite}
                  className={cn(
                    'group relative flex-shrink-0 rounded-md p-0.5 transition-all',
                    isFav ? 'text-amber-300 hover:text-amber-200' : 'text-white/85 hover:text-white',
                  )}
                  aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <Star className={cn('h-4 w-4 transition-all', isFav && 'fill-amber-300')} />
                  <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 dark:bg-gray-700">
                    {isFav ? 'Remove from favourites' : 'Add to favourites'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Centre: pills — absolutely centred on the FULL header width (not
            centred between title and actions), z-10 so they overlay the
            title when a long title meets many pills. max-w keeps them clear
            of the actions slot on the right; flex-wrap + max-h caps the band
            at 2 rows (scrolls beyond). Hidden in vertical nav only; stay
            when minimised. */}
        {!arePillsHidden && pills.length > 0 ? (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex max-h-[62px] w-full max-w-[calc(100%-520px)] -translate-x-1/2 -translate-y-1/2 flex-wrap items-center justify-center gap-1.5 overflow-y-auto [&>*]:pointer-events-auto">
            {renderPills()}
          </div>
        ) : (
          isVerticalNav && hasFilterPills && (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex max-h-[62px] w-full max-w-[calc(100%-520px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center self-center overflow-y-auto [&>*]:pointer-events-auto">
              {renderFilterPills()}
            </div>
          )
        )}

        {/* Right: actions — fixed-width reserved slot (space stays when
            empty) plus optional trailing extras (e.g. help icons). */}
        <div className="flex items-center gap-1.5">
          {renderActions()}
          {actionsExtra}
        </div>
      </div>

      {/* ================================================================ */}
      {/* ROW C — STAT CARDS                                                */}
      {/* Hidden when minimised (chevron) or in vertical nav — except the   */}
      {/* dashboard variant, which keeps stat cards in vertical nav.        */}
      {/* When 0 stat cards, reserve the same height so the header size     */}
      {/* stays consistent across all views (e.g. ImportManager).           */}
      {/* ================================================================ */}
      {!areStatsHidden && (
        <div className="px-4 pb-2">
          {showStatCards ? renderStatCards() : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" aria-hidden="true">
              {/* Height-matching placeholder: 4 empty card-shaped spacers */}
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="min-h-[60px] rounded-lg bg-white/5" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* ROW D — FILTER PILLS + FILTER SLOT                                */}
      {/* Always visible (not affected by minimise or vertical nav).        */}
      {/* filterPills: contiguous pill bar (no gaps).                       */}
      {/* filtersSlot: custom host-rendered filter UI below the pill bar.   */}
      {/* Height is always reserved even when empty, so the header stays    */}
      {/* the same size across all views (matches Row C behaviour).         */}
      {/* ================================================================ */}
      {/* In vertical nav, filter pills render in Row A — Row D only renders
          when a custom filtersSlot exists (no reserved height otherwise). */}
      {(!isVerticalNav || filtersSlot) && (
        <div className={cn('px-4 pb-2', !isVerticalNav && 'min-h-[36px]')}>
          {!isVerticalNav && hasFilterPills && renderFilterPills()}
          {filtersSlot && (
            <div className={!isVerticalNav && hasFilterPills ? 'mt-2' : ''}>
              {filtersSlot}
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* COMBINED LAYOUT TOGGLE (bottom-left)                              */}
      {/* ================================================================ */}
      {showCombinedLayoutToggle && (
        <div className="absolute -bottom-2.5 left-2 z-20 hidden lg:flex lg:items-center lg:gap-0.5">
          <button
            onClick={toggleCombinedLayout}
            className={SHELL_TOGGLE_BUTTON_CLASS}
            title={minimized && sideNavCollapsed ? 'Expand header and sidebar' : 'Minimize header and sidebar'}
          >
            {minimized && sideNavCollapsed ? (
              <ArrowDownRight className={SHELL_TOGGLE_ICON_CLASS} />
            ) : (
              <ArrowUpLeft className={SHELL_TOGGLE_ICON_CLASS} />
            )}
          </button>
        </div>
      )}

      {/* ================================================================ */}
      {/* HELP / TOUR ICON (bottom-right, above chevron)                    */}
      {/* ================================================================ */}
      {(onHelpClick || componentTourConfig) && (
        <button
          onClick={() => {
            if (onHelpClick) {
              onHelpClick();
            } else if (componentTourConfig && typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('guided-tour-open', {
                  detail: {
                    moduleKey: componentTourConfig.moduleKey,
                    flowId: componentTourConfig.flowId,
                    stepId: componentTourConfig.stepId,
                    objectiveId: componentTourConfig.objectiveId,
                  },
                }),
              );
            }
          }}
          className={cn('absolute bottom-8 right-3 z-10', SHELL_TOGGLE_BUTTON_CLASS)}
          title={helpButtonTooltip || componentTourConfig?.tooltip || 'Get help with this section'}
          aria-label={helpButtonTooltip || componentTourConfig?.tooltip || 'Get help with this section'}
        >
          <HelpCircle className={SHELL_TOGGLE_ICON_CLASS} />
        </button>
      )}

      {/* ================================================================ */}
      {/* CHEVRON — minimises Row B/C/D (keeps title bar)                   */}
      {/* ================================================================ */}
      <button
        onClick={toggleMinimized}
        className={cn('absolute -bottom-2.5 right-3 z-10', SHELL_TOGGLE_BUTTON_CLASS)}
        title={minimized ? 'Show stat cards' : 'Hide stat cards'}
      >
        {minimized ? <ChevronDown className={SHELL_TOGGLE_ICON_CLASS} /> : <ChevronUp className={SHELL_TOGGLE_ICON_CLASS} />}
      </button>
    </div>
  );
}

export { StrictModuleHeader };
